import { useState, useEffect, useCallback, useRef } from 'react';

import {
  Avatar,
  Badge,
  ButtonBase,
  Chip,
  Grid,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  Box,
  Typography,
} from '@material-ui/core';
import { makeStyles, alpha } from '@material-ui/core/styles';
import { Search } from '@material-ui/icons';
import PeopleIcon from '@material-ui/icons/People';
import { Skeleton } from '@material-ui/lab';
import { useSnackbar } from 'notistack';
import { useModal } from 'react-modal-hook';
import { useDebouncedCallback } from 'use-debounce';

import { BlockedContactBadge } from 'components/BlockedContactBadge';
import { BlockedContactName } from 'components/BlockedContactName';
import { ContactAvatar } from 'components/ContactAvatar';
import FilterListaAtendimentos from 'components/FilterListaAtendimentos';
import ModalHistoricoAtendimentoContato from 'components/ModalDetalhesAtendimentoFila';
import StyledChannelChipIcon from 'components/StyledChannelChipIcon';
import { CHANNEL_ICON_CONFIG } from 'constants/channel-icon-config';
import { LOCAL_STORAGE_KEY_ORDER_BY_FILA_ESPERA } from 'constants/localStorageKeys';
import * as socketEvents from 'constants/socketEvents';
import { usePersistedOrderBy } from 'hooks/usePersistedOrderBy';
import { Sounds, useSound } from 'hooks/useSound';
import {
  useUrlFilters,
  ORDER_BY_OPTIONS,
  OrderByOption,
} from 'hooks/useUrlFilters';
import socket from 'services/socket';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchFilaV2,
  AtendimentoFilaInterface,
  atendimentosSelector,
  AtendimentoStatusInterface,
  setOrderByFilaDeEspera,
  resetListaAtendimentosNaFilaV2,
  fetchListaAtendimentosNaFilaV2,
} from 'store/reducers/atendimentos';
import {
  ContarNovaMensagemInterface,
  MensagemInterface,
} from 'store/reducers/mensagens';
import { formatWaitTime } from 'utils/formatWaitTime';

import DrawerListaAtendimentos from './components/DrawerListaAtendimentos';

const FIVE_MINUTES_IN_MILLISECONDS = 300000; // 5 min
const MAX_DEBOUNCE_TIME = 30; // 30 segundos

const useStyles = makeStyles(({ palette }) => ({
  chip: {
    cursor: 'pointer',
    color: palette.primary.main,
    backgroundColor: alpha(palette.primary.main, 0.1),
    width: 32,
    height: 32,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiChip-label': {
      padding: 0,
      fontSize: 12,
    },
  },
  tooltip: {
    fontSize: 11,
    maxWidth: 'none',
  },
  badgeUnreadAvatar: {
    position: 'absolute',
    transform: 'scale(0.9) translate(13px, -5px)',
  },
  badgeUnreadRemaining: {
    position: 'absolute',
    transform: 'scale(0.9) translate(13px, 13px)',
  },
}));

interface FilaItemProps {
  atendimento: AtendimentoFilaInterface;
  classes: ReturnType<typeof useStyles>;
  count: number | undefined;
  onSelect: (atendimento: AtendimentoFilaInterface) => void;
}

const FilaItem = ({ atendimento, classes, count, onSelect }: FilaItemProps) => {
  const [updateKey, setUpdateKey] = useState(0);

  const { contact, channel, team, totalMensagensNaoLidas } = atendimento;
  const isBlocked = Boolean(contact.blockedAt || contact.bloqueadoEm);

  return (
    <Grid item>
      <Tooltip
        arrow
        placement="bottom-start"
        classes={{ tooltip: classes.tooltip }}
        onOpen={() => setUpdateKey(prev => prev + 1)}
        title={
          <ListItem
            key={updateKey}
            style={{
              width: 'max-content',
              paddingInline: 12,
              paddingRight: 20,
            }}
          >
            <ListItemAvatar>
              <BlockedContactBadge isBlocked={isBlocked}>
                <ContactAvatar src={contact.foto} />
              </BlockedContactBadge>
            </ListItemAvatar>

            <ListItemText
              primary={
                <>
                  <Typography
                    variant="caption"
                    style={{
                      color: '#fff',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {atendimento.startedIn
                      ? `Em atendimento ${formatWaitTime(
                          atendimento.startedIn,
                        )}`
                      : ''}
                  </Typography>

                  <Box
                    display="flex"
                    alignItems="baseline"
                    justifyContent="space-between"
                    gridGap={24}
                  >
                    <Box display="flex" alignItems="baseline" gridGap={8}>
                      <BlockedContactName
                        name={contact.name}
                        isBlocked={isBlocked}
                        style={{ color: '#fff' }}
                      >
                        {contact.identifier && (
                          <Typography
                            variant="caption"
                            style={{
                              color: '#fff',
                              opacity: 0.6,
                              fontSize: '0.7rem',
                            }}
                          >
                            ({contact.identifier})
                          </Typography>
                        )}
                      </BlockedContactName>
                    </Box>
                  </Box>
                </>
              }
              secondary={
                <Box
                  display="flex"
                  alignItems="center"
                  gridGap={12}
                  style={{ color: '#fff', marginTop: 4 }}
                >
                  <StyledChannelChipIcon
                    type={channel.type as keyof typeof CHANNEL_ICON_CONFIG}
                    label={channel.name}
                    color="#fff"
                  />

                  {team && (
                    <Box display="flex" alignItems="center">
                      <PeopleIcon
                        style={{
                          fontSize: 16,
                          marginRight: 4,
                          color: '#fff',
                        }}
                      />
                      <Typography
                        variant="caption"
                        component="span"
                        style={{ color: '#fff' }}
                      >
                        {team.name}
                      </Typography>
                    </Box>
                  )}
                </Box>
              }
              primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
              secondaryTypographyProps={{
                noWrap: true,
                color: 'inherit',
                variant: 'caption',
              }}
            />
          </ListItem>
        }
      >
        <ButtonBase disableRipple onClick={() => onSelect(atendimento)}>
          <BlockedContactBadge
            isBlocked={isBlocked}
            defaultBadgeContent={
              <Badge
                color="error"
                badgeContent={
                  totalMensagensNaoLidas === -1
                    ? ''
                    : count || totalMensagensNaoLidas
                }
                classes={{ badge: classes.badgeUnreadAvatar }}
              />
            }
          >
            <ContactAvatar src={contact.foto} />
          </BlockedContactBadge>
        </ButtonBase>
      </Tooltip>
    </Grid>
  );
};

export default function FilaEspera() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const {
    filaEsperaV2,
    totalRestanteFilaV2,
    loadingFilaV2,
    totalMessagesUnreadV2,
    orderByFilaDeEspera,
    drawerFilaDeEsperaAberto,
    searchTermFilaDeEspera,
  } = useAppSelector(atendimentosSelector);

  const [atendimentoSelecionado, setAtendimentoSelecionado] =
    useState<AtendimentoFilaInterface>();
  const [countMsgsNaoLidas, setCountMsgsNaoLidas] = useState<{
    [atendimentoId: string]: { total: number };
  }>();
  const { play } = useSound();
  const lastMessagesRef = useRef<{
    [atendimentoId: string]: MensagemInterface;
  }>({});

  const {
    orderBy: urlOrderBy,
    filterOptions,
    setFilterOptions,
  } = useUrlFilters();
  const { orderBy, setOrderBy: persistOrderBy } = usePersistedOrderBy(
    LOCAL_STORAGE_KEY_ORDER_BY_FILA_ESPERA,
    'orderBy',
  );

  const tempoTotalDoDebounce = useRef(0); // VERSÃO NOVA
  // Suprime o som de 'fila-espera' quando ele vem logo após um 'transferencia-forcada'
  const suppressFilaEsperaSoundRef = useRef(false);

  const [debounceIntervalId, setDebounceIntervalId] =
    useState<NodeJS.Timeout | null>(null); // VERSÃO NOVA
  const [estaContando, setEstaContando] = useState(false); // VERSÃO NOVA

  const effectiveOrderBy = orderByFilaDeEspera || orderBy || urlOrderBy;

  // VERSÃO NOVA
  const fetchFilaV2ComOrderBy = useCallback(
    (refreshDrawer = true) => {
      dispatch(fetchFilaV2({ orderBy: effectiveOrderBy }));

      if (refreshDrawer && drawerFilaDeEsperaAberto) {
        dispatch(resetListaAtendimentosNaFilaV2());
        dispatch(
          fetchListaAtendimentosNaFilaV2({
            orderBy: effectiveOrderBy || ORDER_BY_OPTIONS.DEFAULT,
            search: searchTermFilaDeEspera,
          }),
        );
      }
    },
    [
      dispatch,
      effectiveOrderBy,
      drawerFilaDeEsperaAberto,
      searchTermFilaDeEspera,
    ],
  );

  const executeImmediateFetch = useCallback(
    (data?: ContarNovaMensagemInterface) => {
      if (debounceIntervalId) {
        clearInterval(debounceIntervalId);
        setDebounceIntervalId(null);
      }

      setEstaContando(false);

      fetchFilaV2ComOrderBy(true);
      const lastMsg = data
        ? lastMessagesRef.current[data.atendimentoId]
        : undefined;
      if (!lastMsg || lastMsg.autor !== 'AUTO') {
        play(Sounds.NOVA_MENSAGEM);
      }
    },
    [debounceIntervalId, fetchFilaV2ComOrderBy, play],
  );

  const handlerContarMensagem = useDebouncedCallback(
    (data: ContarNovaMensagemInterface) => {
      if (debounceIntervalId) {
        clearInterval(debounceIntervalId);
        setDebounceIntervalId(null);
      }

      setEstaContando(false);
      tempoTotalDoDebounce.current = 0;

      const { posicao } = data;
      if (posicao !== 'EQUIPE') return;

      const lastMsg = lastMessagesRef.current[data.atendimentoId];
      if (!lastMsg || lastMsg.autor !== 'AUTO') {
        play(Sounds.NOVA_MENSAGEM);
      }
      fetchFilaV2ComOrderBy(true);
    },
    3000,
  );

  const handleNovaMensagem = useCallback(
    (data: ContarNovaMensagemInterface) => {
      if (!estaContando) {
        tempoTotalDoDebounce.current = 0;
        setEstaContando(true);

        const id = setInterval(() => {
          const next = tempoTotalDoDebounce.current + 1;

          if (next >= MAX_DEBOUNCE_TIME) {
            executeImmediateFetch(data);
            tempoTotalDoDebounce.current = 0;
            return;
          }

          tempoTotalDoDebounce.current = next;
        }, 1000);

        setDebounceIntervalId(id);
      }

      handlerContarMensagem(data);
    },
    [estaContando, handlerContarMensagem, executeImmediateFetch],
  );

  const handleSetOrderBy = useCallback(
    (orderByValue: OrderByOption) => {
      dispatch(setOrderByFilaDeEspera(orderByValue));
      setFilterOptions(options => ({ ...options, orderBy: orderByValue }));
      persistOrderBy(orderByValue);
    },
    [dispatch, setFilterOptions, persistOrderBy],
  );

  const handleSelectAtendimento = (atendimento: AtendimentoFilaInterface) => {
    setAtendimentoSelecionado(atendimento);
    showDrawerDadosAtendimento();
  };

  const [showDrawerDadosAtendimento, hideDrawerDadosAtendimento] = useModal(
    ({ in: open }) => {
      if (!atendimentoSelecionado) return null;

      return (
        <ModalHistoricoAtendimentoContato
          atendimento={atendimentoSelecionado}
          open={open}
          onClose={hideDrawerDadosAtendimento}
        />
      );
    },
    [atendimentoSelecionado],
  );

  const [showDrawerLista, hideDrawerLista] = useModal(({ in: open }) => (
    <DrawerListaAtendimentos open={open} onClose={hideDrawerLista} />
  ));

  useEffect(() => {
    dispatch(setOrderByFilaDeEspera(orderBy));
  }, [dispatch, orderBy]);

  useEffect(() => {
    fetchFilaV2ComOrderBy(false);
  }, [fetchFilaV2ComOrderBy]);

  useEffect(() => {
    setCountMsgsNaoLidas(undefined);
  }, [filaEsperaV2]);

  // Emite um som de tempo em tempo enquanto houver atendimentos na fila,
  // para sinalizar ao atendente que ainda restam pessoas para serem atendidas.
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (filaEsperaV2.length > 0) play(Sounds.FILA_ESPERA_TIMEOUT);
    }, FIVE_MINUTES_IN_MILLISECONDS);

    return () => {
      clearInterval(intervalId);
    };
  }, [filaEsperaV2.length, play]);

  const debouncedFetchFilaV2 = useDebouncedCallback(() => {
    fetchFilaV2ComOrderBy();
  }, 500);

  useEffect(() => {
    // Observa as mudança de status de atendimento que interfere na fila de espera
    const handlerAtendimentoStatus = (data: AtendimentoStatusInterface) => {
      const { status, atendimento, motivo, finalizadoPor } = data;
      const { id, contato } = atendimento;

      setCountMsgsNaoLidas(prev => ({ ...prev, [id]: { total: 0 } }));

      // exibe um snackbar
      const notify = () => {
        if (motivo === 'INATIVIDADE_ATENDENTE') {
          if (finalizadoPor === 'ADMINISTRADOR') {
            enqueueSnackbar(
              `O administrador finalizou o atendimento de ${contato?.nome}!`,
            );
          } else if (finalizadoPor === 'CORECHAT') {
            enqueueSnackbar(
              `O atendimento de ${contato?.nome}, foi finalizado por inatividade do atendente.`,
            );
          } else {
            enqueueSnackbar(
              `O atendimento de ${contato?.nome}, foi colocado de volta na fila por inatividade do atendente.`,
            );
          }
        } else if (motivo === 'INATIVIDADE_CONTATO') {
          enqueueSnackbar(
            `O atendimento de ${contato?.nome}, foi finalizado por inatividade do contato.`,
          );
        }
      };

      switch (status) {
        case 'iniciado':
        case 'iniciado-por-mim':
          debouncedFetchFilaV2();
          break;
        case 'transferencia-forcada':
          // Sinaliza para suprimir o som do evento 'fila-espera' que vem logo em seguida
          suppressFilaEsperaSoundRef.current = true;
          setTimeout(() => {
            suppressFilaEsperaSoundRef.current = false;
          }, 2000);
          debouncedFetchFilaV2();
          break;
        case 'finalizado-por-mim':
        case 'transferido-fila-espera':
        case 'transferido-outro-atendente':
          notify();
          break;
        case 'fila-espera':
          if (!suppressFilaEsperaSoundRef.current) play(Sounds.FILA_ESPERA);
          debouncedFetchFilaV2();
          if (motivo === 'INATIVIDADE_ATENDENTE') notify();
          break;
        case 'finalizado':
          if (atendimento.posicao === 'ATENDENTE') notify();
          else debouncedFetchFilaV2();
          break;
        default:
          break;
      }
    };

    // Atualiza a fila de espera quando reconectar o socket
    const handlerReconnect = () => {
      fetchFilaV2ComOrderBy(true);
    };

    const handleReceber = (msg: MensagemInterface) => {
      lastMessagesRef.current[msg.atendimentoId] = msg;
    };

    socket.on(socketEvents.ON_RECEBER_MENSAGEM, handleReceber);
    socket.on(socketEvents.ON_CONTAR_NOVA_MENSAGEM, handleNovaMensagem); // VERSÃO NOVA
    socket.on(socketEvents.ON_ATENDIMENTO_STATUS, handlerAtendimentoStatus);
    socket.on(socketEvents.ON_RECONNECT, handlerReconnect);

    return () => {
      socket.off(socketEvents.ON_RECEBER_MENSAGEM, handleReceber);
      socket.off(socketEvents.ON_CONTAR_NOVA_MENSAGEM, handleNovaMensagem); // VERSÃO NOVA
      socket.off(socketEvents.ON_ATENDIMENTO_STATUS, handlerAtendimentoStatus);
      socket.off(socketEvents.ON_RECONNECT, handlerReconnect);
    };
  }, [
    debouncedFetchFilaV2,
    enqueueSnackbar,
    fetchFilaV2ComOrderBy,
    handleNovaMensagem,
    play,
  ]);

  useEffect(() => {
    return () => {
      if (debounceIntervalId) {
        clearInterval(debounceIntervalId);
      }
    };
  }, [debounceIntervalId]);

  const items =
    loadingFilaV2 && filaEsperaV2.length === 0
      ? Array.from(new Array(filaEsperaV2.length) as AtendimentoFilaInterface[])
      : filaEsperaV2;

  const totalUnreadInVisible = filaEsperaV2.reduce((acc, curr) => {
    const count = countMsgsNaoLidas?.[curr.id]?.total;
    const value = count ?? curr.totalMensagensNaoLidas ?? 0;
    return acc + (value > 0 ? value : 0);
  }, 0);

  const totalUnreadRemaining = Math.max(
    0,
    totalMessagesUnreadV2 - totalUnreadInVisible,
  );

  return (
    <Grid container alignItems="center" spacing={1}>
      <FilterListaAtendimentos
        filteredOptions={{ ...filterOptions, orderBy: orderByFilaDeEspera }}
        handleSetOrderBy={handleSetOrderBy}
        size="small"
      />

      {items.map((atendimento, index) => {
        const key = atendimento ? atendimento.id : index;

        if (atendimento) {
          const count = countMsgsNaoLidas?.[atendimento.id]?.total;

          return (
            <FilaItem
              key={key}
              atendimento={atendimento}
              classes={classes}
              count={count}
              onSelect={handleSelectAtendimento}
            />
          );
        }

        return (
          <Grid key={key} item>
            <Skeleton variant="circle">
              <Avatar />
            </Skeleton>
          </Grid>
        );
      })}

      {/* Exibe o botão para visualizar o restante dos atendimentos na fila */}
      {totalRestanteFilaV2 > 0 && (
        <Grid item>
          <Tooltip
            arrow
            title="Ver todos na fila"
            classes={{ tooltip: classes.tooltip }}
          >
            <ButtonBase disableRipple onClick={showDrawerLista}>
              <Badge
                color="error"
                badgeContent={totalUnreadRemaining}
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                classes={{ badge: classes.badgeUnreadRemaining }}
              >
                <Chip
                  className={classes.chip}
                  label={`+${totalRestanteFilaV2}`}
                />
              </Badge>
            </ButtonBase>
          </Tooltip>
        </Grid>
      )}

      {filaEsperaV2.length > 0 && (
        <Grid item>
          <Tooltip
            arrow
            title="Pesquisar na fila"
            classes={{ tooltip: classes.tooltip }}
          >
            <ButtonBase disableRipple onClick={showDrawerLista}>
              <Search style={{ marginTop: 3, marginLeft: 6 }} color="primary" />
            </ButtonBase>
          </Tooltip>
        </Grid>
      )}
    </Grid>
  );
}
